import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { providersApi, ProviderViewDetails, ProviderClientAccount, ProviderIntegrationType } from '../api/providers';
import { adminApi } from '../api/admin';
import { ArrowLeft, Building2, Users, Wallet, Landmark, X, Eye, Receipt, Plug } from 'lucide-react';
import { formatPublicIdDisplay } from '../utils/formatPublicId';

const ACCOUNT_TYPE_META: Record<string, { display: string; purpose: string }> = {
  SETTLEMENT:           { display: 'Settlement',            purpose: 'Main account at bank / provider' },
  CLEARING:             { display: 'Clearing',              purpose: 'Intermediate processing layer' },
  SUSPENSE:             { display: 'Suspense',              purpose: 'Temporary hold for unclear amounts' },
  ERROR:                { display: 'Error',                 purpose: 'Errors pending investigation' },
  FEE_INCOME:           { display: 'Fee Income',            purpose: 'Commission income' },
  FX_INCOME:            { display: 'FX Income',             purpose: 'FX conversion income' },
  OTHER_INCOME:         { display: 'Other Income',          purpose: 'Miscellaneous income' },
  PROVIDER_FEE_EXPENSE: { display: 'Provider Fee Expense',  purpose: 'Provider commission expenses' },
  PROVIDER_FX_EXPENSE:  { display: 'Provider FX Expense',   purpose: 'Provider FX expenses' },
};

const ORDER = ['SETTLEMENT','CLEARING','SUSPENSE','ERROR','FEE_INCOME','FX_INCOME','OTHER_INCOME','PROVIDER_FEE_EXPENSE','PROVIDER_FX_EXPENSE'];

const scrollbarHideStyles = `
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

function formatMoney(value: number, currency?: string) {
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}

function ProviderFeeFixedAmountFields({
  mode,
  fixed,
  fixedCurrency,
  paymentCurrency,
  currencyOptions,
  onFixedChange,
  onFixedCurrencyChange,
}: {
  mode: 'fixed' | 'percent';
  fixed: string;
  fixedCurrency: string;
  paymentCurrency: string;
  currencyOptions: string[];
  onFixedChange: (value: string) => void;
  onFixedCurrencyChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3">
      <label className="block text-sm">
        <span className="text-gray-600 font-medium">Fixed fee</span>
        <input
          type="number"
          step="any"
          value={fixed}
          onChange={(e) => onFixedChange(e.target.value)}
          disabled={mode !== 'fixed'}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
        />
      </label>
      <label className="block text-sm">
        <span className="text-gray-600 font-medium">Fee currency</span>
        <select
          value={fixedCurrency}
          onChange={(e) => onFixedCurrencyChange(e.target.value)}
          disabled={mode !== 'fixed'}
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
        >
          <option value="">Same as payment ({paymentCurrency || '—'})</option>
          {currencyOptions.map((code) => (
            <option key={code} value={code}>{code}</option>
          ))}
        </select>
        <span className="text-xs text-gray-500 mt-1 block">
          Optional. Use when provider charges a fixed cost in another currency.
        </span>
      </label>
    </div>
  );
}

export default function ProviderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<ProviderViewDetails | null>(null);
  const [activeCurrencyTab, setActiveCurrencyTab] = useState<string>('');
  const [activeSubTab, setActiveSubTab] = useState<'banking' | 'ledger' | 'clients' | 'fees' | 'integration'>('banking');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
  const [loadingAccount, setLoadingAccount] = useState(false);

  const [feeMode, setFeeMode] = useState<'fixed' | 'percent'>('fixed');
  const [feeFixed, setFeeFixed] = useState('0');
  const [feeFixedCurrency, setFeeFixedCurrency] = useState('');
  const [feePercent, setFeePercent] = useState('0');
  const [feeMin, setFeeMin] = useState('0');
  const [feeMax, setFeeMax] = useState('0');
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMessage, setFeeMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [inFeeMode, setInFeeMode] = useState<'fixed' | 'percent'>('fixed');
  const [inFeeFixed, setInFeeFixed] = useState('0');
  const [inFeeFixedCurrency, setInFeeFixedCurrency] = useState('');
  const [inFeePercent, setInFeePercent] = useState('0');
  const [inFeeMin, setInFeeMin] = useState('0');
  const [inFeeMax, setInFeeMax] = useState('0');
  const [inFeeSaving, setInFeeSaving] = useState(false);
  const [inFeeMessage, setInFeeMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [intFeeMode, setIntFeeMode] = useState<'fixed' | 'percent'>('fixed');
  const [intFeeFixed, setIntFeeFixed] = useState('0');
  const [intFeeFixedCurrency, setIntFeeFixedCurrency] = useState('');
  const [intFeePercent, setIntFeePercent] = useState('0');
  const [intFeeMin, setIntFeeMin] = useState('0');
  const [intFeeMax, setIntFeeMax] = useState('0');
  const [intFeeSaving, setIntFeeSaving] = useState(false);
  const [intFeeMessage, setIntFeeMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [dOutFeeMode, setDOutFeeMode] = useState<'fixed' | 'percent'>('fixed');
  const [dOutFeeFixed, setDOutFeeFixed] = useState('0');
  const [dOutFeeFixedCurrency, setDOutFeeFixedCurrency] = useState('');
  const [dOutFeePercent, setDOutFeePercent] = useState('0');
  const [dOutFeeMin, setDOutFeeMin] = useState('0');
  const [dOutFeeMax, setDOutFeeMax] = useState('0');
  const [dOutFeeSaving, setDOutFeeSaving] = useState(false);
  const [dOutFeeMessage, setDOutFeeMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [dInFeeMode, setDInFeeMode] = useState<'fixed' | 'percent'>('fixed');
  const [dInFeeFixed, setDInFeeFixed] = useState('0');
  const [dInFeeFixedCurrency, setDInFeeFixedCurrency] = useState('');
  const [dInFeePercent, setDInFeePercent] = useState('0');
  const [dInFeeMin, setDInFeeMin] = useState('0');
  const [dInFeeMax, setDInFeeMax] = useState('0');
  const [dInFeeSaving, setDInFeeSaving] = useState(false);
  const [dInFeeMessage, setDInFeeMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [integrationType, setIntegrationType] = useState<ProviderIntegrationType>(null);
  const [integrationUnitId, setIntegrationUnitId] = useState('');
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);
  const [integrationMessage, setIntegrationMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const getActiveProviderCurrencyId = useCallback(() => {
    const providerCurrencies = data?.provider?.currencies || [];
    if (providerCurrencies.length === 0) return '';

    const selectedCode = (activeCurrencyTab || providerCurrencies[0]?.currency || '').trim().toUpperCase();
    const byCode = providerCurrencies.find((c) => (c.currency || '').trim().toUpperCase() === selectedCode);
    const fallback = providerCurrencies[0];
    return ((byCode?.currencyId || fallback?.currencyId || '') as string).trim();
  }, [data?.provider?.currencies, activeCurrencyTab]);

  const handleViewAccount = async (accountId: string) => {
    try {
      setLoadingAccount(true);
      const response = await adminApi.getClientAccountById(accountId);
      setSelectedAccount(response.data);
    } catch (err: any) {
      console.error('Failed to load account details:', err);
    } finally {
      setLoadingAccount(false);
    }
  };

  const handleSaveOutgoingFee = async () => {
    if (!id) return;
    setFeeSaving(true);
    setFeeMessage(null);
    try {
      await providersApi.updateOutgoingPaymentFee(id, {
        currencyId: getActiveProviderCurrencyId() || undefined,
        mode: feeMode,
        fixedAmount: Number(feeFixed) || 0,
        fixedFeeCurrencyId: feeFixedCurrency || undefined,
        percentOfAmount: Number(feePercent) || 0,
        minFee: Number(feeMin) || 0,
        maxFee: Number(feeMax) || 0,
      });
      const res = await providersApi.getViewDetails(id);
      setData(res);
      setFeeMessage({ type: 'ok', text: 'Settings saved.' });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Could not save';
      setFeeMessage({ type: 'err', text: String(msg) });
    } finally {
      setFeeSaving(false);
    }
  };

  const handleSaveIncomingFee = async () => {
    if (!id) return;
    setInFeeSaving(true);
    setInFeeMessage(null);
    try {
      await providersApi.updateIncomingPaymentFee(id, {
        currencyId: getActiveProviderCurrencyId() || undefined,
        mode: inFeeMode,
        fixedAmount: Number(inFeeFixed) || 0,
        fixedFeeCurrencyId: inFeeFixedCurrency || undefined,
        percentOfAmount: Number(inFeePercent) || 0,
        minFee: Number(inFeeMin) || 0,
        maxFee: Number(inFeeMax) || 0,
      });
      const res = await providersApi.getViewDetails(id);
      setData(res);
      setInFeeMessage({ type: 'ok', text: 'Incoming fee settings saved.' });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Could not save';
      setInFeeMessage({ type: 'err', text: String(msg) });
    } finally {
      setInFeeSaving(false);
    }
  };

  const handleSaveDigitalOutgoingFee = async () => {
    if (!id) return;
    setDOutFeeSaving(true);
    setDOutFeeMessage(null);
    try {
      await providersApi.updateDigitalOutgoingPaymentFee(id, {
        currencyId: getActiveProviderCurrencyId() || undefined,
        mode: dOutFeeMode,
        fixedAmount: Number(dOutFeeFixed) || 0,
        fixedFeeCurrencyId: dOutFeeFixedCurrency || undefined,
        percentOfAmount: Number(dOutFeePercent) || 0,
        minFee: Number(dOutFeeMin) || 0,
        maxFee: Number(dOutFeeMax) || 0,
      });
      const res = await providersApi.getViewDetails(id);
      setData(res);
      setDOutFeeMessage({ type: 'ok', text: 'OFT outgoing fee settings saved.' });
    } catch (err: any) {
      const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || 'Could not save';
      setDOutFeeMessage({ type: 'err', text: String(msg) });
    } finally {
      setDOutFeeSaving(false);
    }
  };

  const handleSaveDigitalIncomingFee = async () => {
    if (!id) return;
    setDInFeeSaving(true);
    setDInFeeMessage(null);
    try {
      await providersApi.updateDigitalIncomingPaymentFee(id, {
        currencyId: getActiveProviderCurrencyId() || undefined,
        mode: dInFeeMode,
        fixedAmount: Number(dInFeeFixed) || 0,
        fixedFeeCurrencyId: dInFeeFixedCurrency || undefined,
        percentOfAmount: Number(dInFeePercent) || 0,
        minFee: Number(dInFeeMin) || 0,
        maxFee: Number(dInFeeMax) || 0,
      });
      const res = await providersApi.getViewDetails(id);
      setData(res);
      setDInFeeMessage({ type: 'ok', text: 'OFT incoming fee settings saved.' });
    } catch (err: any) {
      const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || 'Could not save';
      setDInFeeMessage({ type: 'err', text: String(msg) });
    } finally {
      setDInFeeSaving(false);
    }
  };

  const handleSaveInternalFee = async () => {
    if (!id) return;
    setIntFeeSaving(true);
    setIntFeeMessage(null);
    try {
      await providersApi.updateInternalPaymentFee(id, {
        currencyId: getActiveProviderCurrencyId() || undefined,
        mode: intFeeMode,
        fixedAmount: Number(intFeeFixed) || 0,
        fixedFeeCurrencyId: intFeeFixedCurrency || undefined,
        percentOfAmount: Number(intFeePercent) || 0,
        minFee: Number(intFeeMin) || 0,
        maxFee: Number(intFeeMax) || 0,
      });
      const res = await providersApi.getViewDetails(id);
      setData(res);
      setIntFeeMessage({ type: 'ok', text: 'Internal transfer fee settings saved.' });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Could not save';
      setIntFeeMessage({ type: 'err', text: String(msg) });
    } finally {
      setIntFeeSaving(false);
    }
  };

  const loadIntegration = useCallback(async () => {
    if (!id) return;
    setIntegrationLoading(true);
    try {
      const integration = await providersApi.getIntegration(id);
      setIntegrationType(integration?.type ?? null);
      setIntegrationUnitId(integration?.unitId ?? '');
    } catch (err: any) {
      console.error('Failed to load provider integration:', err);
    } finally {
      setIntegrationLoading(false);
    }
  }, [id]);

  const handleSaveIntegration = async () => {
    if (!id) return;
    setIntegrationSaving(true);
    setIntegrationMessage(null);
    try {
      await providersApi.updateIntegration(id, {
        type: integrationType,
        unitId: integrationType ? (integrationUnitId.trim() || null) : null,
      });
      await loadIntegration();
      setIntegrationMessage({
        type: 'ok',
        text: integrationType
          ? `Provider connected to ${integrationType} API.`
          : 'Provider disconnected from API (manual mode).',
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === 'string' ? err.response.data : null) ||
        err?.message ||
        'Could not save';
      setIntegrationMessage({ type: 'err', text: String(msg) });
    } finally {
      setIntegrationSaving(false);
    }
  };

  useEffect(() => {
    loadIntegration();
  }, [loadIntegration]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    providersApi.getViewDetails(id)
      .then(res => {
        setData(res);
        const firstCurrency = res.provider.currencies?.[0]?.currency || res.ledgerAccounts?.[0]?.currencyId || '';
        setActiveCurrencyTab(firstCurrency);
      })
      .catch(err => setError(err?.response?.data?.message || err.message || 'Failed to load provider details'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!data?.provider) return;

    const currencyId = getActiveProviderCurrencyId();
    const currencyKey = currencyId.toUpperCase();
    const outgoingByCurrency = data.provider.outgoingPaymentFeesByCurrency || {};
    const incomingByCurrency = data.provider.incomingPaymentFeesByCurrency || {};
    const internalByCurrency = data.provider.internalPaymentFeesByCurrency || {};

    const f = (currencyKey ? outgoingByCurrency[currencyKey] : undefined) ?? data.provider.outgoingPaymentFee;
    const m = f?.mode?.toLowerCase();
    setFeeMode(m === 'percent' || m === 'percentage' ? 'percent' : 'fixed');
    setFeeFixed(String(f?.fixedAmount ?? 0));
    setFeeFixedCurrency(String(f?.fixedFeeCurrencyId ?? ''));
    setFeePercent(String(f?.percentOfAmount ?? 0));
    setFeeMin(String(f?.minFee ?? 0));
    setFeeMax(String(f?.maxFee ?? 0));
    setFeeMessage(null);

    const inf = (currencyKey ? incomingByCurrency[currencyKey] : undefined) ?? data.provider.incomingPaymentFee;
    const im = inf?.mode?.toLowerCase();
    setInFeeMode(im === 'percent' || im === 'percentage' ? 'percent' : 'fixed');
    setInFeeFixed(String(inf?.fixedAmount ?? 0));
    setInFeeFixedCurrency(String(inf?.fixedFeeCurrencyId ?? ''));
    setInFeePercent(String(inf?.percentOfAmount ?? 0));
    setInFeeMin(String(inf?.minFee ?? 0));
    setInFeeMax(String(inf?.maxFee ?? 0));
    setInFeeMessage(null);

    const intf = (currencyKey ? internalByCurrency[currencyKey] : undefined) ?? data.provider.internalPaymentFee;
    const intm = intf?.mode?.toLowerCase();
    setIntFeeMode(intm === 'percent' || intm === 'percentage' ? 'percent' : 'fixed');
    setIntFeeFixed(String(intf?.fixedAmount ?? 0));
    setIntFeeFixedCurrency(String(intf?.fixedFeeCurrencyId ?? ''));
    setIntFeePercent(String(intf?.percentOfAmount ?? 0));
    setIntFeeMin(String(intf?.minFee ?? 0));
    setIntFeeMax(String(intf?.maxFee ?? 0));
    setIntFeeMessage(null);

    const digitalOutByCurrency = data.provider.digitalOutgoingPaymentFeesByCurrency || {};
    const dout = (currencyKey ? digitalOutByCurrency[currencyKey] : undefined) ?? data.provider.digitalOutgoingPaymentFee;
    const doutm = dout?.mode?.toLowerCase();
    setDOutFeeMode(doutm === 'percent' || doutm === 'percentage' ? 'percent' : 'fixed');
    setDOutFeeFixed(String(dout?.fixedAmount ?? 0));
    setDOutFeeFixedCurrency(String(dout?.fixedFeeCurrencyId ?? ''));
    setDOutFeePercent(String(dout?.percentOfAmount ?? 0));
    setDOutFeeMin(String(dout?.minFee ?? 0));
    setDOutFeeMax(String(dout?.maxFee ?? 0));
    setDOutFeeMessage(null);

    const digitalInByCurrency = data.provider.digitalIncomingPaymentFeesByCurrency || {};
    const din = (currencyKey ? digitalInByCurrency[currencyKey] : undefined) ?? data.provider.digitalIncomingPaymentFee;
    const dinm = din?.mode?.toLowerCase();
    setDInFeeMode(dinm === 'percent' || dinm === 'percentage' ? 'percent' : 'fixed');
    setDInFeeFixed(String(din?.fixedAmount ?? 0));
    setDInFeeFixedCurrency(String(din?.fixedFeeCurrencyId ?? ''));
    setDInFeePercent(String(din?.percentOfAmount ?? 0));
    setDInFeeMin(String(din?.minFee ?? 0));
    setDInFeeMax(String(din?.maxFee ?? 0));
    setDInFeeMessage(null);
  }, [data, activeCurrencyTab, getActiveProviderCurrencyId]);

  const provider = data?.provider;
  const currencyCodeByKey = useMemo(() => {
    const map = new Map<string, string>();
    const push = (key?: string | null, code?: string | null) => {
      const k = (key || '').trim();
      const c = (code || '').trim();
      if (!k || !c) return;
      map.set(k.toUpperCase(), c.toUpperCase());
    };

    for (const c of provider?.currencies || []) {
      const resolvedCode = (c.currency || c.currencyId || '').trim();
      push(c.currencyId, resolvedCode);
      push(c.currency, resolvedCode);
    }
    for (const t of data?.totalsByCurrency || []) {
      const resolvedCode = (t.currencyCode || t.currencyId || '').trim();
      push(t.currencyId, resolvedCode);
      push(t.currencyCode, resolvedCode);
    }
    for (const r of data?.ledgerAccounts || []) {
      const value = (r.currencyId || '').trim();
      if (!value) continue;
      if (!map.has(value.toUpperCase())) map.set(value.toUpperCase(), value.toUpperCase());
    }
    return map;
  }, [provider?.currencies, data?.totalsByCurrency, data?.ledgerAccounts]);

  const resolveCurrencyCode = useCallback((raw?: string | null) => {
    const value = (raw || '').trim();
    if (!value) return '';
    return currencyCodeByKey.get(value.toUpperCase()) || value.toUpperCase();
  }, [currencyCodeByKey]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-indigo-600 rounded-full" />
          Loading provider details…
        </div>
      </div>
    );
  }

  if (error || !data || !provider) {
    return (
      <div className="p-6">
        <button onClick={() => navigate('/providers')} className="text-indigo-600 hover:text-indigo-800 mb-4 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to providers
        </button>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error || 'Provider not found'}</div>
      </div>
    );
  }

  const currencyCodes = Array.from(new Set([
    ...data.ledgerAccounts.map(r => resolveCurrencyCode(r.currencyId)),
    ...(provider.currencies?.map(c => resolveCurrencyCode(c.currency || c.currencyId)) || []),
  ])).filter(Boolean);

  const currentCurrency = activeCurrencyTab || currencyCodes[0] || '';
  const tabAccounts = data.ledgerAccounts.filter(r => resolveCurrencyCode(r.currencyId) === currentCurrency);
  const accountByType = Object.fromEntries(tabAccounts.map(r => [r.accountTypeCode, r]));
  const settlementBalances = data.ledgerAccounts
    .filter(r => r.accountTypeCode === 'SETTLEMENT')
    .map(r => ({
      currency: resolveCurrencyCode(r.currencyId),
      balance: r.balance ?? 0,
    }));
  const settlementAccount = accountByType.SETTLEMENT;
  const tabCurrencyTotal = data.totalsByCurrency.find(t => resolveCurrencyCode(t.currencyCode || t.currencyId) === currentCurrency);
  const tabProviderCurrency = provider.currencies?.find(c => resolveCurrencyCode(c.currency || c.currencyId) === currentCurrency);
  const bd = tabProviderCurrency?.bankDetails;
  const activeProviderCurrencyKey = (getActiveProviderCurrencyId() || '').trim().toUpperCase();
  const hasOutgoingOverride = Boolean(
    provider.outgoingPaymentFee || (activeProviderCurrencyKey && provider.outgoingPaymentFeesByCurrency?.[activeProviderCurrencyKey])
  );
  const hasIncomingOverride = Boolean(
    provider.incomingPaymentFee || (activeProviderCurrencyKey && provider.incomingPaymentFeesByCurrency?.[activeProviderCurrencyKey])
  );
  const hasInternalOverride = Boolean(
    provider.internalPaymentFee || (activeProviderCurrencyKey && provider.internalPaymentFeesByCurrency?.[activeProviderCurrencyKey])
  );

  const clientsForCurrency = data.clientAccounts?.filter(
    c => resolveCurrencyCode(c.currencyCode || c.currencyId) === currentCurrency
  ) || [];

  const filteredClients = clientSearch.trim()
    ? clientsForCurrency.filter(c =>
        c.clientFullName.toLowerCase().includes(clientSearch.toLowerCase()) ||
        (c.accountName?.toLowerCase() || '').includes(clientSearch.toLowerCase()) ||
        (c.accountNumber?.toLowerCase() || '').includes(clientSearch.toLowerCase())
      )
    : clientsForCurrency;

  return (
    <>
      <style>{scrollbarHideStyles}</style>
      <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/providers')} className="text-indigo-600 hover:text-indigo-800 mb-4 flex items-center gap-1 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to providers
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{provider.name}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  (provider.status || 'ACTIVE') === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  {provider.status || 'ACTIVE'}
                </span>
                <span>·</span>
                <span>{provider.currencies?.length || 0} currencies</span>
                <span>·</span>
                <span>{data.clientAccounts?.length || 0} client accounts</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Settlement ledger balance</div>
            <div className="text-2xl font-bold text-gray-900">
              {settlementBalances.length > 0
                ? settlementBalances.map(({ balance, currency }) => formatMoney(balance, currency)).join(' · ')
                : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Currency tabs */}
      {currencyCodes.length > 0 && (
        <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto hide-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {currencyCodes.map(code => {
            const total = data.totalsByCurrency.find(t => resolveCurrencyCode(t.currencyCode || t.currencyId) === code)?.totalBalance ?? 0;
            return (
              <button
                key={code}
                type="button"
                onClick={() => {
                  setActiveCurrencyTab(code);
                  setActiveSubTab('banking');
                  setClientSearch('');
                }}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  currentCurrency === code
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="font-semibold">{code}</span>
                <span className="ml-2 text-xs text-gray-400 font-normal">Net ledger {formatMoney(total)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          type="button"
          onClick={() => setActiveSubTab('banking')}
          className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeSubTab === 'banking' ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Landmark className="w-4 h-4" /> Banking Details
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('ledger')}
          className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeSubTab === 'ledger' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Wallet className="w-4 h-4" /> Ledger Accounts
          {tabAccounts.length > 0 && <span className="ml-1 text-xs text-gray-400">({tabAccounts.length})</span>}
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('clients')}
          className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeSubTab === 'clients' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4" /> Clients
          {clientsForCurrency.length > 0 && <span className="ml-1 text-xs text-gray-400">({clientsForCurrency.length})</span>}
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('fees')}
          className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeSubTab === 'fees' ? 'border-amber-600 text-amber-800' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Receipt className="w-4 h-4" /> Fees (incoming / outgoing / internal)
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('integration')}
          className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeSubTab === 'integration' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Plug className="w-4 h-4" /> Integration
          {integrationType && (
            <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              {integrationType}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {activeSubTab === 'banking' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Banking Details — {currentCurrency}</h2>
            {!bd ? (
              <p className="text-amber-600">No banking details configured for {currentCurrency}.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">Account name</span><div className="font-medium">{bd.accountName || '—'}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">Account number</span><div className="font-medium font-mono">{bd.accountNumber || '—'}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">Bank</span><div className="font-medium">{bd.bankName || '—'}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">SWIFT</span><div className="font-medium font-mono">{bd.swiftCode || '—'}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">Branch / Bank code</span><div className="font-medium">{bd.branchCode || '—'} / {bd.bankCode || '—'}</div></div>
                <div className="bg-gray-50 rounded-lg p-3"><span className="text-gray-500 text-xs">Location</span><div className="font-medium">{bd.bankLocation || '—'}</div></div>
                {bd.bankAddress && (
                  <div className="col-span-2 md:col-span-3 bg-gray-50 rounded-lg p-3">
                    <span className="text-gray-500 text-xs">Bank address</span>
                    <div className="font-medium">{bd.bankAddress}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'ledger' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Ledger Accounts — {currentCurrency}</h2>
              <div className="flex items-start gap-6 text-right">
                <div>
                  <span className="text-sm text-gray-500">Settlement ledger balance</span>
                  <div className="font-mono font-semibold text-gray-900">
                    {settlementAccount ? formatMoney(settlementAccount.balance ?? 0, currentCurrency) : '—'}
                  </div>
                </div>
                {tabCurrencyTotal && (
                  <div>
                    <span className="text-sm text-gray-500">Net ledger position</span>
                    <div className="font-mono font-semibold text-gray-900">{formatMoney(tabCurrencyTotal.totalBalance, currentCurrency)}</div>
                  </div>
                )}
              </div>
            </div>
            {ORDER.length === 0 || tabAccounts.length === 0 ? (
              <p className="text-gray-500">No ledger accounts linked for {currentCurrency}.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Display name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Purpose</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ORDER.map(typeCode => {
                      const row = accountByType[typeCode];
                      const meta = ACCOUNT_TYPE_META[typeCode];
                      return (
                        <tr key={typeCode} className={row ? '' : 'opacity-40'}>
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">{typeCode}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">{meta?.display ?? typeCode} <span className="text-gray-400 font-normal">{currentCurrency}</span></td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{meta?.purpose ?? ''}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm">
                            {row ? formatMoney(row.balance ?? 0) : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'fees' && (
          <div className="space-y-10">
            <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Outgoing payment fee — {currentCurrency || 'Currency'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              Applies only to this provider. If not overridden, the backend uses global configuration values (
              <code className="text-xs bg-gray-100 px-1 rounded">Payments:OutgoingFee</code>).
              {!hasOutgoingOverride && (
                <span className="block mt-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  No provider-specific override — global defaults apply. Save the form below to set custom values.
                </span>
              )}
            </p>
            {feeMessage && (
              <div
                className={`mb-4 text-sm rounded-lg px-3 py-2 ${
                  feeMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
                }`}
              >
                {feeMessage.text}
              </div>
            )}
            <div className="grid gap-4 max-w-xl">
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Mode</span>
                <select
                  value={feeMode}
                  onChange={(e) => setFeeMode(e.target.value as 'fixed' | 'percent')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of amount</option>
                </select>
              </label>
              <ProviderFeeFixedAmountFields
                mode={feeMode}
                fixed={feeFixed}
                fixedCurrency={feeFixedCurrency}
                paymentCurrency={currentCurrency}
                currencyOptions={currencyCodes}
                onFixedChange={setFeeFixed}
                onFixedCurrencyChange={setFeeFixedCurrency}
              />
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Fraction of amount</span>
                <input
                  type="number"
                  step="any"
                  value={feePercent}
                  onChange={(e) => setFeePercent(e.target.value)}
                  disabled={feeMode !== 'percent'}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Fraction of the payment amount, e.g. <code className="bg-gray-100 px-0.5">0.005</code> = 0.5%.
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Min. fee</span>
                  <input
                    type="number"
                    step="any"
                    value={feeMin}
                    onChange={(e) => setFeeMin(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Max. fee (0 = no cap)</span>
                  <input
                    type="number"
                    step="any"
                    value={feeMax}
                    onChange={(e) => setFeeMax(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSaveOutgoingFee}
                  disabled={feeSaving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                >
                  {feeSaving ? 'Saving…' : 'Save outgoing'}
                </button>
              </div>
            </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Incoming payment fee — {currentCurrency || 'Currency'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              Configured separately from outgoing; stored in{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">metadata.incomingFee</code>. Global defaults:{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">Payments:IncomingFee</code>. When an incoming payment is
              registered, the fee is withheld: net posts to Pending/Suspense; the fee posts to Fee Income.
              {!hasIncomingOverride && (
                <span className="block mt-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  No provider override — global IncomingFee settings apply.
                </span>
              )}
            </p>
            {inFeeMessage && (
              <div
                className={`mb-4 text-sm rounded-lg px-3 py-2 ${
                  inFeeMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
                }`}
              >
                {inFeeMessage.text}
              </div>
            )}
            <div className="grid gap-4 max-w-xl">
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Mode</span>
                <select
                  value={inFeeMode}
                  onChange={(e) => setInFeeMode(e.target.value as 'fixed' | 'percent')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of amount</option>
                </select>
              </label>
              <ProviderFeeFixedAmountFields
                mode={inFeeMode}
                fixed={inFeeFixed}
                fixedCurrency={inFeeFixedCurrency}
                paymentCurrency={currentCurrency}
                currencyOptions={currencyCodes}
                onFixedChange={setInFeeFixed}
                onFixedCurrencyChange={setInFeeFixedCurrency}
              />
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Fraction of amount</span>
                <input
                  type="number"
                  step="any"
                  value={inFeePercent}
                  onChange={(e) => setInFeePercent(e.target.value)}
                  disabled={inFeeMode !== 'percent'}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Fraction of the gross incoming amount, e.g. <code className="bg-gray-100 px-0.5">0.005</code> = 0.5%.
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Min. fee</span>
                  <input
                    type="number"
                    step="any"
                    value={inFeeMin}
                    onChange={(e) => setInFeeMin(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Max. (0 = no cap)</span>
                  <input
                    type="number"
                    step="any"
                    value={inFeeMax}
                    onChange={(e) => setInFeeMax(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSaveIncomingFee}
                  disabled={inFeeSaving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                  {inFeeSaving ? 'Saving…' : 'Save incoming'}
                </button>
              </div>
            </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">OFT Outgoing payment fee — {currentCurrency || 'Currency'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              Fee applied to OFT Outgoing payments for this provider. Stored in{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">metadata.digitalOutgoingFee</code>.
              If not set, no OFT outgoing fee is charged.
            </p>
            {dOutFeeMessage && (
              <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${dOutFeeMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                {dOutFeeMessage.text}
              </div>
            )}
            <div className="grid gap-4 max-w-xl">
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Mode</span>
                <select
                  value={dOutFeeMode}
                  onChange={(e) => setDOutFeeMode(e.target.value as 'fixed' | 'percent')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of amount</option>
                </select>
              </label>
              <ProviderFeeFixedAmountFields
                mode={dOutFeeMode}
                fixed={dOutFeeFixed}
                fixedCurrency={dOutFeeFixedCurrency}
                paymentCurrency={currentCurrency}
                currencyOptions={currencyCodes}
                onFixedChange={setDOutFeeFixed}
                onFixedCurrencyChange={setDOutFeeFixedCurrency}
              />
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Fraction of amount</span>
                <input
                  type="number"
                  step="any"
                  value={dOutFeePercent}
                  onChange={(e) => setDOutFeePercent(e.target.value)}
                  disabled={dOutFeeMode !== 'percent'}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Fraction of the payment amount, e.g. <code className="bg-gray-100 px-0.5">0.005</code> = 0.5%.
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Min. fee</span>
                  <input
                    type="number"
                    step="any"
                    value={dOutFeeMin}
                    onChange={(e) => setDOutFeeMin(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Max. fee (0 = no cap)</span>
                  <input
                    type="number"
                    step="any"
                    value={dOutFeeMax}
                    onChange={(e) => setDOutFeeMax(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSaveDigitalOutgoingFee}
                  disabled={dOutFeeSaving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                >
                  {dOutFeeSaving ? 'Saving…' : 'Save OFT outgoing'}
                </button>
              </div>
            </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">OFT Incoming payment fee — {currentCurrency || 'Currency'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              Fee applied to OFT Incoming payments for this provider. Stored in{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">metadata.digitalIncomingFee</code>.
              If not set, no OFT incoming fee is charged.
            </p>
            {dInFeeMessage && (
              <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${dInFeeMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                {dInFeeMessage.text}
              </div>
            )}
            <div className="grid gap-4 max-w-xl">
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Mode</span>
                <select
                  value={dInFeeMode}
                  onChange={(e) => setDInFeeMode(e.target.value as 'fixed' | 'percent')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500"
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of amount</option>
                </select>
              </label>
              <ProviderFeeFixedAmountFields
                mode={dInFeeMode}
                fixed={dInFeeFixed}
                fixedCurrency={dInFeeFixedCurrency}
                paymentCurrency={currentCurrency}
                currencyOptions={currencyCodes}
                onFixedChange={setDInFeeFixed}
                onFixedCurrencyChange={setDInFeeFixedCurrency}
              />
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Fraction of amount</span>
                <input
                  type="number"
                  step="any"
                  value={dInFeePercent}
                  onChange={(e) => setDInFeePercent(e.target.value)}
                  disabled={dInFeeMode !== 'percent'}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Fraction of the gross incoming amount, e.g. <code className="bg-gray-100 px-0.5">0.005</code> = 0.5%.
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Min. fee</span>
                  <input
                    type="number"
                    step="any"
                    value={dInFeeMin}
                    onChange={(e) => setDInFeeMin(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Max. fee (0 = no cap)</span>
                  <input
                    type="number"
                    step="any"
                    value={dInFeeMax}
                    onChange={(e) => setDInFeeMax(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSaveDigitalIncomingFee}
                  disabled={dInFeeSaving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50"
                >
                  {dInFeeSaving ? 'Saving…' : 'Save OFT incoming'}
                </button>
              </div>
            </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Internal transfer fee — {currentCurrency || 'Currency'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              Fee for transfers between registered clients within the same provider and currency (XChangeLab Pay / INTERNAL).
              Stored in{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">metadata.internalFee</code>. Global defaults:{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">Payments:InternalFee</code>. Tier rules for{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">INTERNAL_TRANSFER</code> override these settings when present.
              {!hasInternalOverride && (
                <span className="block mt-2 text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  No provider override — global InternalFee settings apply.
                </span>
              )}
            </p>
            {intFeeMessage && (
              <div
                className={`mb-4 text-sm rounded-lg px-3 py-2 ${
                  intFeeMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
                }`}
              >
                {intFeeMessage.text}
              </div>
            )}
            <div className="grid gap-4 max-w-xl">
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Mode</span>
                <select
                  value={intFeeMode}
                  onChange={(e) => setIntFeeMode(e.target.value as 'fixed' | 'percent')}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of amount</option>
                </select>
              </label>
              <ProviderFeeFixedAmountFields
                mode={intFeeMode}
                fixed={intFeeFixed}
                fixedCurrency={intFeeFixedCurrency}
                paymentCurrency={currentCurrency}
                currencyOptions={currencyCodes}
                onFixedChange={setIntFeeFixed}
                onFixedCurrencyChange={setIntFeeFixedCurrency}
              />
              <label className="block text-sm">
                <span className="text-gray-600 font-medium">Fraction of amount</span>
                <input
                  type="number"
                  step="any"
                  value={intFeePercent}
                  onChange={(e) => setIntFeePercent(e.target.value)}
                  disabled={intFeeMode !== 'percent'}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Fraction of the transfer amount, e.g. <code className="bg-gray-100 px-0.5">0.005</code> = 0.5%.
                </span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Min. fee</span>
                  <input
                    type="number"
                    step="any"
                    value={intFeeMin}
                    onChange={(e) => setIntFeeMin(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Max. fee (0 = no cap)</span>
                  <input
                    type="number"
                    step="any"
                    value={intFeeMax}
                    onChange={(e) => setIntFeeMax(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </label>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleSaveInternalFee}
                  disabled={intFeeSaving}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {intFeeSaving ? 'Saving…' : 'Save internal'}
                </button>
              </div>
            </div>
            </div>
          </div>
        )}

        {activeSubTab === 'integration' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">API Integration</h2>
            <p className="text-sm text-gray-600 mb-4">
              Connect this provider to an external payment API. When connected, outgoing payments, incoming
              collections and FX orders routed to this provider are processed automatically through the API.
              Without a connection the provider works in manual back-office mode.
            </p>
            {integrationMessage && (
              <div
                className={`mb-4 text-sm rounded-lg px-3 py-2 ${
                  integrationMessage.type === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
                }`}
              >
                {integrationMessage.text}
              </div>
            )}
            {integrationLoading ? (
              <p className="text-gray-500 text-sm">Loading integration settings…</p>
            ) : (
              <div className="grid gap-4 max-w-xl">
                <label className="block text-sm">
                  <span className="text-gray-600 font-medium">Integration</span>
                  <select
                    value={integrationType ?? ''}
                    onChange={(e) => {
                      setIntegrationType((e.target.value || null) as ProviderIntegrationType);
                      setIntegrationMessage(null);
                    }}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">None (manual processing)</option>
                    <option value="PYVIO">Pyvio</option>
                  </select>
                </label>
                {integrationType === 'PYVIO' && (
                  <label className="block text-sm">
                    <span className="text-gray-600 font-medium">Unit ID (sub-merchant, optional)</span>
                    <input
                      type="text"
                      value={integrationUnitId}
                      onChange={(e) => setIntegrationUnitId(e.target.value)}
                      placeholder="Leave empty unless Pyvio issued a sub-merchant unit id"
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                    />
                  </label>
                )}
                {integrationType === 'PYVIO' && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    Only one provider can be connected to Pyvio at a time. API credentials (App ID, App Secret,
                    RSA keys) are configured on the backend.
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    onClick={handleSaveIntegration}
                    disabled={integrationSaving}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {integrationSaving ? 'Saving…' : integrationType ? 'Connect' : 'Save (manual mode)'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'clients' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Client Accounts — {currentCurrency}</h2>
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-64"
                />
                {clientsForCurrency.length > 0 && (
                  <div className="text-right">
                    <span className="text-sm text-gray-500">{clientsForCurrency.length} clients · Total</span>
                    <div className="font-mono font-semibold text-gray-900">
                      {formatMoney(clientsForCurrency.reduce((sum, c) => sum + (c.totalBalance || 0), 0), currentCurrency)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {filteredClients.length === 0 ? (
              <p className="text-gray-500">
                {clientSearch.trim() ? 'No clients match your search.' : `No client accounts for ${currentCurrency}.`}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Available</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Blocked</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredClients.map((client: ProviderClientAccount) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{client.clientFullName}</div>
                          {client.clientPublicId && <div className="text-xs text-gray-400">ID: {formatPublicIdDisplay(client.clientPublicId)}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-700">{client.accountName || '—'}</div>
                          {client.accountNumber && <div className="text-xs font-mono text-gray-400">{client.accountNumber}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            client.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {client.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{formatMoney(client.availableBalance ?? 0)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-amber-600">
                          {(client.blockedBalance ?? 0) > 0 ? formatMoney(client.blockedBalance ?? 0) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold">{formatMoney(client.totalBalance ?? 0)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleViewAccount(client.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded transition-colors text-sm"
                            title="View account details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Account Details Modal */}
    {selectedAccount && (
      <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto z-50">
        <div className="min-h-screen flex items-start justify-center py-10 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl">
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Account Details</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-sm text-gray-600">{selectedAccount.clientFullName}</span>
                  {selectedAccount.clientPublicId && (
                    <span className="text-xs font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
                      {formatPublicIdDisplay(selectedAccount.clientPublicId)}
                    </span>
                  )}
                  {selectedAccount.providerName && (
                    <span className="text-xs text-gray-400">· {selectedAccount.providerName}</span>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedAccount(null)} className="text-gray-400 hover:text-gray-600 mt-1">
                <X className="h-6 w-6" />
              </button>
            </div>

            {loadingAccount ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Summary Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account ID</p>
                    <p className="text-xs font-mono text-gray-700 break-all">{selectedAccount.id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Currency</p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      {resolveCurrencyCode(selectedAccount.currencyId)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedAccount.status?.toUpperCase() === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                      selectedAccount.status?.toUpperCase() === 'FROZEN' ? 'bg-blue-100 text-blue-800' :
                      selectedAccount.status?.toUpperCase() === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      selectedAccount.status?.toUpperCase() === 'RESTRICTED' ? 'bg-orange-100 text-orange-800' :
                      selectedAccount.status?.toUpperCase() === 'CLOSED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedAccount.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Balance</p>
                    <p className="text-xl font-bold text-blue-700 mt-1">
                      {(selectedAccount.totalBalance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="text-sm font-normal text-blue-400 ml-1">{resolveCurrencyCode(selectedAccount.currencyId)}</span>
                    </p>
                  </div>
                </div>

                {/* Banking Details */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Banking Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Account Name</p>
                      <p className="text-sm text-gray-800">{selectedAccount.accountName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Account Number</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.accountNumber || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">IBAN</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.iban || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Bank Name</p>
                      <p className="text-sm text-gray-800">{selectedAccount.bankName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">SWIFT Code</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.swiftCode || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Bank Location</p>
                      <p className="text-sm text-gray-800">{selectedAccount.bankLocation || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Branch Code</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.branchCode || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Bank Code</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.bankCode || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Intermediary Bank Name</p>
                      <p className="text-sm text-gray-800">{selectedAccount.intermediaryBankName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Intermediary Bank SWIFT</p>
                      <p className="text-sm text-gray-800 font-mono">{selectedAccount.intermediaryBankSwift || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Provider</p>
                      <p className="text-sm text-gray-800">{selectedAccount.providerName || '—'}</p>
                    </div>
                    {selectedAccount.bankAddress && (
                      <div className="sm:col-span-2">
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Bank Address</p>
                        <p className="text-sm text-gray-800">{selectedAccount.bankAddress}</p>
                      </div>
                    )}
                    {!selectedAccount.accountName && !selectedAccount.accountNumber && !selectedAccount.bankName && (
                      <div className="sm:col-span-2 text-sm text-gray-400 italic">No banking details configured.</div>
                    )}
                  </div>
                </div>

                {/* Sub-Accounts */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Sub-Accounts</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {([
                      { label: 'Available', data: selectedAccount.availableAccount, color: 'green' },
                      { label: 'Blocked', data: selectedAccount.blockedAccount, color: 'red' },
                      { label: 'Incoming Pending', data: selectedAccount.incomingPendingAccount, color: 'blue' },
                      { label: 'Outgoing Pending', data: selectedAccount.outgoingPendingAccount, color: 'orange' },
                      { label: 'Restricted', data: selectedAccount.restrictedAccount, color: 'gray' },
                    ]).map(({ label, data, color }) => (
                      <div key={label} className="border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-4 hover:bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-medium text-gray-900">{label}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              data?.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {data?.status ?? '—'}
                            </span>
                          </div>
                          {data?.id && <p className="text-xs text-gray-400 font-mono truncate">{data.id}</p>}
                          {data?.displayName && <p className="text-xs text-gray-400">{data.displayName}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-base font-bold ${
                            color === 'green' ? 'text-green-600' :
                            color === 'red' ? 'text-red-600' :
                            color === 'blue' ? 'text-blue-600' :
                            color === 'orange' ? 'text-orange-600' :
                            'text-gray-600'
                          }`}>
                            {((data)?.balance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-gray-400">{resolveCurrencyCode(data?.currencyCode || selectedAccount.currencyId)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Close button */}
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => setSelectedAccount(null)}
                    className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
